<template>
  <SidePopup
    :title="title"
    v-on:close="onClose"
    @submit="onSubmit"
    :loading="isLoading"
    :routeback="routeBackPath"
  >
    <h4 class="text-center">Dados básicos</h4>
    <LineDivider></LineDivider>
    <FormAutocomplete
      v-if="isAdmin"
      label="Selecione uma empresa"
      v-model="form.empresa"
      :options="empresasOptions"
      :disabled="isEdit"
      class="mt-3 mb-2"
      hint="Selecione uma empresa para associar esta conta de usuário."
      :persistenthint="true"
      :validation="$v.form.empresa"
    />
    <FormField
      label="Nome do usuário"
      v-model="form.nome"
      :validation="$v.form.nome"
    />
    <FormField
      label="E-mail do usuário"
      v-model="form.email"
      typeField="email"
      :validation="$v.form.email"
    />
    <FormSwitch label="Usuário ativo" v-model="form.ativo" class="mt-2" />

    <h4 class="text-center mt-4">Vendedor</h4>
    <LineDivider></LineDivider>
    <FormField
      label="Código ERP"
      v-model="form.id_usuario_erp"
      hint="Obrigatório para definir o tipo de vendedor"
      :persistenthint="true"
      @change="onErpChange"
    />
    <FormAutocomplete
      label="Tipo de vendedor"
      v-model="form.tipo_vendedor"
      :options="tipoVendedorOptions"
      :disabled="!hasErpCode"
      clearable
      class="mt-2"
    />
    <FormField
      label="Telefone 1"
      v-model="form.telefone1"
      :mask="maskTelefone1"
      class="mt-2"
    />
    <FormField
      label="Telefone 2"
      v-model="form.telefone2"
      :mask="maskTelefone2"
      class="mt-2"
    />

    <template v-if="showPerfilLifeGrowth">
      <h4 class="text-center mt-4">Perfil LifeGrowth</h4>
      <LineDivider></LineDivider>
      <FormAutocomplete
        label="Perfil de usuário"
        v-model="form.idperfil_usuario"
        :options="perfilUsuarioOptions"
        :disabled="!hasErpCode"
        clearable
        class="mt-2"
        hint="Requer código ERP. Deixe vazio para remover o vínculo."
        :persistenthint="true"
      />
    </template>

    <template v-if="showPasswordFields">
      <h4 class="text-center mt-4">Senha</h4>
      <LineDivider></LineDivider>
      <p v-if="isEdit" class="text-caption mb-2">
        Se o campo de alterar senha for preenchido, a senha será alterada.
      </p>
      <FormPassword
        :label="isEdit ? 'Nova senha' : 'Senha'"
        v-model="form.pwd"
        :validation="$v.form.pwd"
      />
      <FormPassword
        label="Confirme a senha"
        v-model="form.pwdc"
        :validation="$v.form.pwdc"
      />
    </template>

    <template v-if="canEditPermissoes">
      <h4 class="text-center mt-4">Permissões</h4>
      <LineDivider></LineDivider>
      <UsuarioPermissoesTree
        :itensMenu="permissoesState.itensMenu"
        :fornecedores="permissoesState.fornecedores"
        :flags="permissoesState.flags"
        @change="onPermissoesChange"
      />
    </template>
    <p v-else-if="isEdit && !isAdmin" class="mt-4 text-caption">
      Você não pode alterar as próprias permissões nesta tela.
    </p>
    <p
      v-else-if="isAdmin && !isEdit && !form.empresa"
      class="mt-4 text-caption"
    >
      Selecione uma empresa para carregar as permissões disponíveis.
    </p>

    <template v-slot:buttons>
      <FormButton
        v-if="showApproveAcademy"
        text="Aprovar academy"
        type="secundario"
        @click="onApproveAcademy"
      />
      <FormButton text="Salvar" submit type="submissao" />
    </template>
  </SidePopup>
</template>

<script>
import SidePopup from "@/components/SidePopup";
import LineDivider from "@/components/LineDivider";
import FormButton from "@/components/form/FormButton";
import FormField from "@/components/form/FormField";
import FormPassword from "@/components/form/FormPassword";
import FormSwitch from "@/components/form/FormSwitch";
import FormAutocomplete from "@/components/form/FormAutocomplete";
import UsuarioPermissoesTree from "@/pages/configuracoes/usuario/UsuarioPermissoesTree";
import { mapGetters } from "vuex";
import {
  USUARIO_LOAD,
  USUARIO_LOAD_ESTRUTURA,
  USUARIO_NEW,
  USUARIO_UPDATE,
  USUARIO_APPROVE
} from "@/store/actions/usuario";
import { EMPRESA_LOAD_TAGS, EMPRESA_LOAD_ALL } from "@/store/actions/empresa";
import { ModulesEnum } from "@/store/actions/modulesEnum";
import Api from "@/service/api-web";
import { required, email, sameAs } from "vuelidate/lib/validators";

const TIPO_VENDEDOR_OPTIONS = [
  { text: "Interno", value: "INTERNO" },
  { text: "Externo", value: "EXTERNO" }
];

const requiredIfPassword = (value, vm) => {
  if (!vm.showPasswordFields || !vm.form.pwd) {
    return true;
  }
  return required(value);
};

const requiredPasswordOnCreate = (value, vm) => {
  if (!vm.showPasswordFields || vm.isEdit) {
    return true;
  }
  return required(value);
};

const requiredIfAdminCreate = (value, vm) => {
  if (!vm.isAdmin || vm.isEdit) {
    return true;
  }
  return required(value);
};

export default {
  name: "UsuarioForm",
  components: {
    SidePopup,
    LineDivider,
    FormButton,
    FormField,
    FormPassword,
    FormSwitch,
    FormAutocomplete,
    UsuarioPermissoesTree
  },
  props: {
    isAdmin: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    isLoading: false,
    empresaTags: [],
    empresasRaw: [],
    form: {
      empresa: null,
      nome: "",
      email: "",
      pwd: "",
      pwdc: "",
      id_usuario_erp: "",
      tipo_vendedor: "",
      telefone1: "",
      telefone2: "",
      ativo: true,
      idperfil_usuario: null
    },
    perfisUsuarioRaw: [],
    permissoesState: {
      itensMenu: [],
      fornecedores: [],
      flags: {
        permissao_atendente: false,
        permissao_alterarsenha: false
      }
    },
    tipoVendedorOptions: TIPO_VENDEDOR_OPTIONS
  }),
  validations() {
    return {
      form: {
        empresa: { requiredIfAdminCreate },
        nome: { required },
        email: { required, email },
        pwd: { requiredPasswordOnCreate },
        pwdc: { requiredIfPassword, sameAsPwd: sameAs("pwd") }
      }
    };
  },
  computed: {
    ...mapGetters([
      "getEmpresaId",
      "getFornecedorId",
      "getUsuarioId",
      "moduloFornecedorAtivo"
    ]),
    isEdit() {
      return Boolean(this.$route.params.idusuario);
    },
    idusuario() {
      return this.$route.params.idusuario;
    },
    title() {
      return this.isEdit ? "Editar usuário" : "Novo usuário";
    },
    routeBackPath() {
      return this.isAdmin
        ? "/admin/usuarios"
        : "/fornecedor/configuracoes/usuarios";
    },
    empresasOptions() {
      return (this.empresasRaw || [])
        .slice()
        .sort((a, b) => a.nome.localeCompare(b.nome))
        .map(empresa => ({
          text: empresa.nome,
          value: empresa.idempresa
        }));
    },
    selectedEmpresaData() {
      if (!this.form.empresa) {
        return null;
      }
      return (
        (this.empresasRaw || []).find(
          empresa => empresa.idempresa === this.form.empresa
        ) || null
      );
    },
    contextEmpresaId() {
      if (this.isAdmin) {
        return this.form.empresa;
      }
      return this.getEmpresaId;
    },
    contextFornecedorId() {
      if (!this.isAdmin) {
        return this.getFornecedorId;
      }
      return this.resolveFornecedorId(this.selectedEmpresaData);
    },
    hasErpCode() {
      return Boolean(String(this.form.id_usuario_erp || "").trim());
    },
    empresaUsaKeycloak() {
      return (this.empresaTags || []).some(
        tag => tag.id === "use-keycloak-login" && tag.active
      );
    },
    showPasswordFields() {
      return !this.empresaUsaKeycloak;
    },
    canEditPermissoes() {
      if (this.isAdmin) {
        return Boolean(this.contextEmpresaId);
      }
      if (!this.isEdit) {
        return true;
      }
      return this.getUsuarioId !== this.idusuario;
    },
    showApproveAcademy() {
      return this.isAdmin && this.isEdit;
    },
    maskTelefone1() {
      return this.getPhoneMask(this.form.telefone1);
    },
    maskTelefone2() {
      return this.getPhoneMask(this.form.telefone2);
    },
    showPerfilLifeGrowth() {
      return (
        this.moduloFornecedorAtivo([ModulesEnum.LIFEGROWTH]) &&
        Boolean(this.contextFornecedorId)
      );
    },
    perfilUsuarioOptions() {
      return (this.perfisUsuarioRaw || []).map(perfil => ({
        text: perfil.nome,
        value: perfil.idperfil
      }));
    }
  },
  watch: {
    contextFornecedorId: {
      immediate: true,
      handler(id) {
        this.loadPerfisUsuario(id);
      }
    },
    "form.empresa"(value, oldValue) {
      if (!this.isAdmin || this.isEdit || !value || value === oldValue) {
        return;
      }
      this.onEmpresaSelected();
    }
  },
  mounted() {
    if (this.isAdmin) {
      this.bootstrapAdminForm();
      return;
    }
    this.loadEmpresaTags();
    this.loadFormData();
  },
  methods: {
    getPhoneMask(phone) {
      return phone && phone.replace(/\D/g, "").length > 11
        ? ["(##) ####-####", "(##) #####-####", "## (##) #####-####"]
        : ["(##) ####-####", "(##) #####-####"];
    },
    bootstrapAdminForm() {
      this.isLoading = true;
      this.$store
        .dispatch(EMPRESA_LOAD_ALL)
        .then(data => {
          this.empresasRaw = data || [];
          if (this.isEdit) {
            this.form.empresa = this.$route.query.idempresa || null;
            if (!this.form.empresa) {
              this.$vueOnToast.pop("error", "Empresa do usuário não informada");
              this.onClose(true);
              return;
            }
            this.loadEmpresaTags();
            this.loadFormData();
            return;
          }
          this.isLoading = false;
        })
        .catch(() => {
          this.$vueOnToast.pop("error", "Falha ao listar as empresas");
          this.isLoading = false;
        });
    },
    resolveFornecedorId(empresaData) {
      if (!empresaData) {
        return null;
      }
      const fornecedores = empresaData.fornecedores || [];
      const ativo = fornecedores.find(fornecedor => fornecedor.ativo);
      if (ativo) {
        return ativo.idfornecedor;
      }
      if (empresaData.idfornecedor_faturamento) {
        return empresaData.idfornecedor_faturamento;
      }
      if (fornecedores.length) {
        return fornecedores[0].idfornecedor;
      }
      return null;
    },
    buildStoreContext() {
      return {
        idempresa: this.contextEmpresaId,
        idfornecedor: this.contextFornecedorId
      };
    },
    loadEmpresaTags() {
      if (!this.contextEmpresaId) {
        this.empresaTags = [];
        return;
      }
      this.$store
        .dispatch(EMPRESA_LOAD_TAGS, { idempresa: this.contextEmpresaId })
        .then(data => {
          this.empresaTags = data || [];
        })
        .catch(() => {
          this.empresaTags = [];
        });
    },
    onEmpresaSelected() {
      this.form.id_usuario_erp = "";
      this.form.tipo_vendedor = "";
      this.form.telefone1 = "";
      this.form.telefone2 = "";
      this.form.pwd = "";
      this.form.pwdc = "";
      this.permissoesState = {
        itensMenu: [],
        fornecedores: [],
        flags: {
          permissao_atendente: false,
          permissao_alterarsenha: false
        }
      };
      this.loadEmpresaTags();
      this.loadFormData();
    },
    loadFormData() {
      if (this.isAdmin && !this.isEdit && !this.form.empresa) {
        return;
      }
      if (this.isAdmin && !this.contextEmpresaId) {
        return;
      }

      this.isLoading = true;
      const ctx = this.buildStoreContext();
      const action = this.isEdit
        ? this.$store.dispatch(USUARIO_LOAD, {
            idusuario: this.idusuario,
            ...ctx
          })
        : this.$store.dispatch(USUARIO_LOAD_ESTRUTURA, ctx);

      action
        .then(data => this.applyFormData(data))
        .catch(err => {
          const erro =
            (err.response && err.response.data && err.response.data.error) ||
            "Erro ao carregar formulário de usuário";
          this.$vueOnToast.pop("error", erro);
          this.onClose(true);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    applyFormData(data) {
      this.form = {
        ...this.form,
        nome: data.nome || "",
        email: data.nomeusuario || "",
        pwd: "",
        pwdc: "",
        id_usuario_erp: data.id_usuario_erp || "",
        tipo_vendedor: data.tipo_vendedor || "",
        telefone1: data.telefone1 || "",
        telefone2: data.telefone2 || "",
        ativo: data.ativo !== false,
        empresa: data.idempresa || this.form.empresa,
        idperfil_usuario: data.idperfil_usuario || null
      };

      const flags = {
        permissao_atendente: Boolean(
          data.permissoes && data.permissoes.permissao_atendente
        ),
        permissao_alterarsenha: Boolean(
          data.permissoes && data.permissoes.permissao_alterarsenha
        )
      };

      this.permissoesState = {
        itensMenu: (data.itens_menu || []).map(item => ({ ...item })),
        fornecedores: (data.fornecedores_granted || []).map(item => ({
          ...item
        })),
        flags
      };
    },
    onErpChange() {
      if (!this.hasErpCode) {
        this.form.tipo_vendedor = "";
        this.form.idperfil_usuario = null;
      }
    },
    loadPerfisUsuario(idfornecedor) {
      this.perfisUsuarioRaw = [];
      if (
        !idfornecedor ||
        !this.moduloFornecedorAtivo([ModulesEnum.LIFEGROWTH])
      ) {
        return;
      }
      Api.v4.perfilUsuario
        .listPerfis(idfornecedor)
        .then(resp => {
          const data = resp?.data?.data || resp?.data || [];
          this.perfisUsuarioRaw = Array.isArray(data) ? data : [];
        })
        .catch(() => {
          this.perfisUsuarioRaw = [];
        });
    },
    onPermissoesChange(state) {
      this.permissoesState = {
        itensMenu: state.itensMenu || [],
        fornecedores: state.fornecedores || [],
        flags: state.flags || {
          permissao_atendente: false,
          permissao_alterarsenha: false
        }
      };
    },
    buildPermissoesPayload() {
      if (!this.canEditPermissoes) {
        return undefined;
      }
      return {
        itens: (this.permissoesState.itensMenu || [])
          .filter(item => item.tem_permissao)
          .map(item => item.iditem),
        fornecedores: (this.permissoesState.fornecedores || [])
          .filter(item => item.fornecedor_granted)
          .map(item => item.idfornecedor),
        permissao_atendente: Boolean(
          this.permissoesState.flags.permissao_atendente
        ),
        permissao_alterarsenha: Boolean(
          this.permissoesState.flags.permissao_alterarsenha
        )
      };
    },
    prepareData() {
      const payload = {
        nome: this.form.nome,
        email: this.form.email,
        empresa: this.contextEmpresaId,
        id_usuario_erp: this.hasErpCode
          ? String(this.form.id_usuario_erp).trim()
          : null,
        tipo_vendedor: this.form.tipo_vendedor || null,
        telefone1: String(this.form.telefone1 || "").trim() || null,
        telefone2: String(this.form.telefone2 || "").trim() || null,
        ativo: Boolean(this.form.ativo),
        idempresa: this.contextEmpresaId,
        idfornecedor: this.contextFornecedorId
      };

      if (this.isAdmin) {
        payload.fromAdmin = true;
      }

      const permissoes = this.buildPermissoesPayload();
      if (permissoes) {
        payload.permissoes = permissoes;
      }

      if (this.showPasswordFields && this.form.pwd) {
        payload.pwd = this.form.pwd;
      }

      if (this.isEdit) {
        payload.idusuario = this.idusuario;
      }

      if (this.showPerfilLifeGrowth) {
        payload.idperfil_usuario = this.form.idperfil_usuario || null;
      }

      return payload;
    },
    onClose(routeBack = false) {
      this.isLoading = false;
      this.$emit("close");
      if (routeBack) {
        this.$router.replace(this.routeBackPath);
      }
    },
    onApproveAcademy() {
      const options = {
        title: `Confirma a aprovação de todas as seções do academy do usuário '${this.form.nome}'?`
      };
      this.confirmSwal(options, () => {
        this.isLoading = true;
        this.$store
          .dispatch(USUARIO_APPROVE, this.idusuario)
          .then(() => {
            this.$vueOnToast.pop("success", "Academy aprovado com sucesso!");
          })
          .catch(() => {
            this.$vueOnToast.pop("error", "Erro ao aprovar academy do usuário");
          })
          .finally(() => {
            this.isLoading = false;
          });
      });
    },
    onSubmit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) {
        return;
      }

      if (this.showPasswordFields && this.form.pwd !== this.form.pwdc) {
        this.$vueOnToast.pop(
          "error",
          "Os campos de senha e de confirmação da senha não são iguais!"
        );
        return;
      }

      this.isLoading = true;
      const data = this.prepareData();
      const action = this.isEdit
        ? this.$store.dispatch(USUARIO_UPDATE, data)
        : this.$store.dispatch(USUARIO_NEW, data);

      action
        .then(() => {
          this.$vueOnToast.pop(
            "success",
            this.isEdit
              ? "Usuário atualizado com sucesso!"
              : "Novo usuário criado com sucesso!"
          );
          this.onClose(true);
        })
        .catch(err => {
          const erro =
            (err.response && err.response.data && err.response.data.error) ||
            (this.isEdit
              ? "Erro ao atualizar usuário"
              : "Erro ao criar novo usuário");
          this.$vueOnToast.pop("error", erro);
          this.isLoading = false;
        });
    }
  }
};
</script>
